import React, { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";

import { useBmapi } from "../utils/bmapi-context";
import { BUSINESS_TYPES, USER_ROLES } from "../utils/constants";
import { getErrorMessageString } from "../utils/errors";
import { common, form, roles, storeTypes } from "../messages";
import { museumLabels } from "../messages/businessMuseums";
import { storeLabels } from "../messages/businessStores";
import { Pagination } from "@material-ui/lab";

const ProvinceList = lazy(() =>
  import(/* webpackChunkName: 'ProvinceList' */ "../components/ProvinceList.js")
);

export const rolesLabels = {
  ASSISTANT: roles.storeAssistant,
  BUSINESS_MANAGER: roles.storeManager,
  CASHIER: roles.operator,
  EDITOR: roles.campaignManager,
  REPORTER: roles.viewer,
  SUBSCRIPTION_MANAGER: roles.subscriptionManager,
  SUBSCRIPTION_PLAN_MANAGER: roles.subscriptionPlanManager,
  SUBSCRIPTION_STAMP_MANAGER: roles.subscriptionStampManager,
};

const INIT_QUERY = {
  page: 1,
  qty: 50,
  status: "",
  category: "",
  display: ["manager", "province", "category"],
  order: "order",
  type: "",
  name: "",
};

export const getPermName = (code, intl) =>
  intl.formatMessage(
    rolesLabels[Object.entries(USER_ROLES).find((r) => r[1] === code)[0]]
  );

function BusinessElement({
  business,
  categories,
  gdspTenant,
  handleSelect,
  isItemSelected,
  campaignBusinessesSet,
}) {
  const categoriesMap = new Map(
    categories?.map((obj) => [obj.id, obj.str_value])
  );

  const category =
    categoriesMap.get(business.attrs?.find((obj) => obj.k === "category")?.v) ||
    "-";

  const province =
    business.attrs?.find((obj) => obj.k === "province")?.v || "-";

  const isAlreadyInCampaign = campaignBusinessesSet.has(business.id);

  const isChecked = isItemSelected || isAlreadyInCampaign;

  const isDisabled = isAlreadyInCampaign;

  return (
    <TableRow
      hover
      role="checkbox"
      aria-checked={isChecked}
      selected={isItemSelected}
      onClick={() => {
        if (!isDisabled) handleSelect(business.id);
      }}
      style={{
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.5 : 1,
        pointerEvents: isDisabled ? "none" : "auto",
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox checked={isChecked} color="primary" disabled={isDisabled} />
      </TableCell>
      <TableCell>{business.name}</TableCell>
      <TableCell>{category}</TableCell>
      {gdspTenant && <TableCell>{province}</TableCell>}
    </TableRow>
  );
}

export default function BusinessSelectDialog({
  type,
  open,
  handleClose,
  addBusinessToCampaign,
  campaignBusinesses,
}) {
  const intl = useIntl();
  const { bmapi, notifyError } = useBmapi();
  const [businesses, setBusinesses] = useState(false);
  const [selected, setSelected] = useState([]);
  const [categories, setCategories] = useState(false);
  const [total, setTotal] = useState(false);
  const [query, setQuery] = useState(INIT_QUERY);
  const [loading, setLoading] = useState(false);

  let provincesList = require(`../configurations/address/provinces-ita.json`);

  const gdspTenant = bmapi.settings.tenantId === "6294348160712791";

  const businessLabels =
    type && type === BUSINESS_TYPES.MUSEUM ? museumLabels : storeLabels;

  const campaignBusinessesSet = new Set(campaignBusinesses.map((b) => b.id));

  const isSelected = (id) => selected.includes(id);

  const handleSelectAllClick = (event) => {
    const businessesToConsider = businesses.filter(
      (bs) => !campaignBusinessesSet.has(bs.id)
    );

    if (event.target.checked) {
      const businessIdsToSelect = businessesToConsider.map((bs) => bs.id);
      setSelected(businessIdsToSelect);
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    setSelected(newSelected);
  };

  const handleQuery = useCallback((label) => {
    const updateValue = (val) => {
      setQuery((v) => ({ ...v, [label]: val }));
    };
    return (i, f) => {
      if (label === "page" || label === "status") updateValue(f);
      else if (i?.target) updateValue(i.target.value);
      else updateValue(i);
    };
  }, []);

  const reset = () => {
    handleClose();
  };

  const add = () => {
    addBusinessToCampaign(selected);
  };

  const updateBusinesses = useCallback(
    (bs /*businessId*/) => {
      const typePar = query.type ?? type;
      let bsFiltered =
        (bs || []).filter(
          (b) =>
            typePar === b.type ||
            (!typePar && b.type === BUSINESS_TYPES.MERCHANT)
        ) || [];
      setBusinesses(bsFiltered);
    },
    [type, query.type]
  );

  const adjustQuery = (q) => ({
    ...q,
    page: q.page - 1,
    // q.page - 1 evita che ultima pagina sia vuota, perché parte da 0
    status: q.status ? 5 : 0,
  });

  const handleLoad = useCallback(
    async (query) => {
      const userInfo = bmapi.getUserInfo();
      if (bmapi.isTenantManager()) {
        try {
          const q = adjustQuery(query);
          setLoading(true);
          const count = await bmapi.getBusinessesCount({
            ...q,
            subscription: bmapi.getUserInfo().business.subscription_id,
          });
          const businesses = await bmapi.getBusinessesAdmin({
            ...q,
            subscription: bmapi.getUserInfo().business.subscription_id,
          });
          setTotal({ count: count });
          updateBusinesses(businesses);
          setLoading(false);
        } catch (e) {
          setLoading(false);
          notifyError(getErrorMessageString(e, intl));
        }
      } else if (userInfo.role === "M") {
        if (userInfo.business) {
          setTotal({ count: 1 });
          updateBusinesses([userInfo.business]);
        } else {
          setTotal({ count: 0 });
          updateBusinesses([]);
        }
      }
    },
    [bmapi, intl, notifyError, updateBusinesses]
  );

  const loadCategories = useCallback(() => {
    bmapi
      .getGenListCategories()
      .then((resp) => {
        if (!resp) {
          resp = [];
        }
        resp.items.sort((a, b) => a.str_value.localeCompare(b.str_value));

        setCategories(resp);
      })
      .catch((e) => {
        if (
          e.httpCode === 404 &&
          getErrorMessageString(e) === "Entity not found"
        ) {
          return;
        }
        notifyError(getErrorMessageString(e, intl));
      });
  }, [bmapi, intl, notifyError]);

  const byName = (a, b) => a.name.localeCompare(b.name);

  useEffect(() => {
    if (open) {
      setQuery(INIT_QUERY);
      setSelected([]);
      handleLoad(INIT_QUERY);
      loadCategories();
    }
  }, [open, handleLoad, loadCategories]);

  const submitQuery = (e) => {
    e.preventDefault();
    let provinceArr = [];
    if (query.region) {
      provincesList.map((obj) =>
        obj.regione === query.region ? provinceArr.push(obj.sigla) : null
      );
    }
    query.prov = provinceArr;
    setQuery((v) => ({ ...v, page: 0 }));
    handleLoad({ ...query, page: 0 });
  };

  const changePage = (goTop) => (_, val) => {
    setQuery((v) => ({ ...v, page: val }));
    handleLoad({ ...query, page: val });
    if (goTop) window.scrollTo(0, 0);
  };

  return (
    <Dialog
      maxWidth="md"
      open={open}
      onClose={handleClose}
      aria-labelledby="dialog-title"
      fullWidth
    >
      <DialogTitle id="dialog-title">
        {intl.formatMessage(businessLabels.pageTitle)}
      </DialogTitle>
      <DialogContent>
        <Card>
          <CardContent>
            <form onSubmit={submitQuery}>
              <TextField
                autoFocus
                margin="dense"
                label={intl.formatMessage(common.name)}
                value={query.name}
                onChange={handleQuery("name")}
                fullWidth
                helperText={
                  query.name && query.name.length > 0 && query.name.length < 3
                    ? "Inserisci almeno 3 caratteri per una ricerca più precisa."
                    : ""
                }
              />
              {categories.items && (
                <TextField
                  margin="dense"
                  label={intl.formatMessage(form.category)}
                  value={query.category}
                  onChange={handleQuery("category")}
                  fullWidth
                  select
                >
                  {(categories.items || []).map((category) => (
                    <MenuItem value={category.id} key={category.str_value}>
                      {storeTypes[category.str_value]
                        ? intl.formatMessage(storeTypes[category.str_value])
                        : category.str_value}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {gdspTenant && (
                <Suspense
                  fallback={<div>...{intl.formatMessage(common.loading)}</div>}
                >
                  <ProvinceList
                    region={query.region}
                    handleValue={handleQuery("region")}
                    margin="dense"
                    label="Regione di appartenenza"
                    name="region"
                  />
                </Suspense>
              )}
              <Box mt={1}>
                <FormControl
                  margin="normal"
                  style={{ float: "right", display: "block" }}
                >
                  <Button variant="contained" color="primary" type="submit">
                    {intl.formatMessage(common.update)}
                  </Button>
                </FormControl>
              </Box>
            </form>
          </CardContent>
        </Card>

        {(!businesses || loading) && <LinearProgress />}
        <Box my={2}>
          {businesses &&
            !loading &&
            (!businesses.length ? (
              <Card>
                <CardContent>
                  <Typography gutterBottom>
                    <FormattedMessage
                      {...businessLabels.noBusinessRegistered}
                    />
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent>
                  {total?.count > INIT_QUERY.qty && (
                    <Box my={3}>
                      <Pagination
                        count={Math.ceil(total?.count / query?.qty)}
                        page={query.page}
                        onChange={changePage(false)}
                        size="small"
                      />
                    </Box>
                  )}
                  <TableContainer>
                    <Table aria-label="simple table" size="small">
                      <caption>
                        Tabella {intl.formatMessage(businessLabels.pageTitle)}
                      </caption>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            padding="checkbox"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  color="primary"
                                  indeterminate={
                                    selected.length > 0 &&
                                    selected.length <
                                      businesses.filter(
                                        (bs) =>
                                          !campaignBusinessesSet.has(bs.id)
                                      ).length
                                  }
                                  checked={
                                    businesses.length > 0 &&
                                    selected.length ===
                                      businesses.filter(
                                        (bs) =>
                                          !campaignBusinessesSet.has(bs.id)
                                      ).length &&
                                    !(
                                      businesses.length > 0 &&
                                      businesses.filter(
                                        (bs) =>
                                          !campaignBusinessesSet.has(bs.id)
                                      ).length === 0 &&
                                      selected.length === 0
                                    )
                                  }
                                  onChange={handleSelectAllClick}
                                />
                              }
                              label={
                                <Typography
                                  variant="body2"
                                  noWrap
                                  style={{ fontWeight: 500 }}
                                >
                                  Seleziona tutti
                                </Typography>
                              }
                            />
                          </TableCell>
                          <TableCell>Nome</TableCell>
                          <TableCell>Categoria</TableCell>
                          {gdspTenant && <TableCell>Provincia</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {businesses.sort(byName).map((b) => {
                          const isItemSelected = isSelected(b.id);
                          return (
                            <BusinessElement
                              key={b.id}
                              business={b}
                              categories={categories.items}
                              gdspTenant={gdspTenant}
                              handleSelect={handleSelect}
                              isItemSelected={isItemSelected}
                              campaignBusinessesSet={campaignBusinessesSet}
                            />
                          );
                        })}
                      </TableBody>
                    </Table>
                    {total?.count > INIT_QUERY.qty && (
                      <Pagination
                        count={Math.ceil(total?.count / query?.qty)}
                        page={query.page}
                        onChange={changePage(true)}
                        size="small"
                      />
                    )}
                  </TableContainer>
                </CardContent>
              </Card>
            ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={reset} variant="outlined">
          {intl.formatMessage(common.close)}
        </Button>
        <Button
          onClick={() => add(selected)}
          color="primary"
          variant="contained"
          disabled={!selected.length}
        >
          {intl.formatMessage(common.add)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
